import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Divider, Link, Paper, TextField, Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { FixedAssetDepreciationService } from '../../services/fixedAssetDepreciation.service';
import { DepreciationRunResult } from '../../interfaces/fixedAsset/fixedAssetReport.interface';
import { toLocalIsoDate } from '../../utils/dateUtils';
import { FixedAssetService } from '../../services/fixedAsset.service';
import { FixedAsset } from '../../interfaces/fixedAsset/fixedAsset.interface';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { monthHasEnded, previousMonthEnd } from './fixedAssetFormRules';

const money = (n: number) =>
  (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// month input value ("YYYY-MM") → first-of-month ISO date ("YYYY-MM-01")
const monthToPeriod = (month: string) => (month ? `${month}-01` : '');

const monthLabel = (iso: string) =>
  new Date(`${iso.slice(0, 7)}-01T00:00:00`).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

// Months after the given month key ("YYYY-MM") up to and including the current month.
const monthsAfter = (key: string, currentKey: string): string[] => {
  const out: string[] = [];
  let [y, m] = key.split('-').map(Number);
  for (let i = 0; i < 60; i++) {
    m += 1;
    if (m > 12) { m = 1; y += 1; }
    const k = `${y}-${String(m).padStart(2, '0')}`;
    if (k > currentKey) break;
    out.push(k);
  }
  return out;
};

const DepreciationRun: React.FC = () => {
  const canRun = usePermission(PERMISSIONS.FIXED_ASSET_DEPRECIATION_RUN); // run = FIXED_ASSET_DEPRECIATION_RUN server-side
  const toast = useToast();
  const [assets, setAssets] = useState<FixedAsset[] | null>(null);

  const loadAssets = async () => {
    const institutionId = AuthService.getSelectedInstitution();
    if (!institutionId) return;
    try {
      const res = await FixedAssetService.listByInstitution(institutionId);
      setAssets(res.content ?? []);
    } catch {
      setAssets([]);
    }
  };
  useEffect(() => { loadAssets(); }, []);

  // Latest month depreciation has been posted through, across active assets.
  const activeAssets = (assets ?? []).filter(a => a.status === 'ACTIVE');
  const throughKeys = activeAssets.map(a => a.depreciatedThrough?.slice(0, 7)).filter((k): k is string => !!k);
  const lastPostedKey = throughKeys.length ? throughKeys.reduce((a, b) => (a > b ? a : b)) : null;
  const oldestKey = throughKeys.length === activeAssets.length && throughKeys.length
    ? throughKeys.reduce((a, b) => (a < b ? a : b)) : null;
  const nowKey = toLocalIsoDate().slice(0, 7);
  const outstanding = oldestKey ? monthsAfter(oldestKey, nowKey) : [];
  const [month, setMonth] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<DepreciationRunResult | null>(null);

  const period = monthToPeriod(month);
  // Polish 17: value events need depreciation posted through exactly the event month
  const monthInProgress = !!period && !monthHasEnded(period, toLocalIsoDate());

  const doRun = async () => {
    setConfirmOpen(false);
    const institutionId = AuthService.getSelectedInstitution();
    try {
      setRunning(true);
      setResult(null);
      const res = await FixedAssetDepreciationService.run(institutionId!, period);
      setResult(res);
      if (res.skippedAssets?.length) {
        toast.showError(`Depreciation run finished — ${res.skippedAssets.length} asset(s) skipped, see below`);
      } else {
        toast.showSuccess('Depreciation run complete');
      }
      loadAssets();
    } catch (e) {
      toast.showApiError(e, 'Failed to run depreciation');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 720 }} data-help-page="fixedAssets.run.page">
      <Typography variant="h5" sx={{ mb: 2 }}>Run Depreciation</Typography>

      {assets && activeAssets.length > 0 && (
        <Alert severity={outstanding.length > 0 || throughKeys.length < activeAssets.length ? 'warning' : 'success'} sx={{ mb: 2 }}>
          {lastPostedKey
            ? `Depreciation posted through ${monthLabel(lastPostedKey)}.`
            : 'No depreciation has been posted yet.'}
          {outstanding.length > 0 && ` Outstanding: ${outstanding.map(k => monthLabel(`${k}-01`)).join(', ')}.`}
          {lastPostedKey && throughKeys.length < activeAssets.length
            && ` ${activeAssets.length - throughKeys.length} active asset(s) have no depreciation posted yet.`}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            data-help="fixedAssets.run.period"
            label="Period"
            type="month"
            value={month}
            onChange={e => setMonth(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ flex: '1 1 200px', maxWidth: 260 }}
          />
          <Button
            data-help="fixedAssets.run.run"
            variant="contained"
            disabled={!canRun || !month || running}
            onClick={() => setConfirmOpen(true)}
          >
            {running ? <CircularProgress size={22} /> : 'Run depreciation'}
          </Button>
        </Box>
        {monthInProgress && (
          <Alert severity="warning" sx={{ mt: 2 }} data-testid="month-in-progress-warning">
            {monthLabel(period)} has not ended yet. Running it now moves depreciation through this month, so value
            events (revaluation, impairment) dated {previousMonthEnd(period)} will be blocked afterwards. Run it
            after the month ends if you plan one.
          </Alert>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          Posts one journal entry per asset for each outstanding month through the selected period.
        </Typography>
      </Paper>

      {result && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Run summary — through {monthLabel(result.period)}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            <Box sx={{ flex: '1 1 30%', minWidth: 160 }}>
              <Typography variant="caption" color="text.secondary">Assets processed</Typography>
              <Typography variant="h6">{result.assetsProcessed}</Typography>
            </Box>
            <Box sx={{ flex: '1 1 30%', minWidth: 160 }}>
              <Typography variant="caption" color="text.secondary">Months posted</Typography>
              <Typography variant="h6">{result.monthsPosted}</Typography>
            </Box>
            <Box sx={{ flex: '1 1 30%', minWidth: 160 }}>
              <Typography variant="caption" color="text.secondary">Total depreciation</Typography>
              <Typography variant="h6">{money(result.totalDepreciation)}</Typography>
            </Box>
          </Box>

          {(result.skippedAssets?.length ?? 0) > 0 && (
            <Alert severity="error" sx={{ mt: 2 }} data-testid="skipped-assets">
              {result.skippedAssets!.length} asset(s) were not depreciated — fix each asset's accounts
              (Edit asset) and run again:
              <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
                {result.skippedAssets!.map(sk => (
                  <li key={sk.assetId}>
                    <Link component={RouterLink} to={`/fixed-assets/${sk.assetId}/edit`}>
                      {sk.assetCode} {sk.assetName}
                    </Link>
                    : {sk.reason}
                  </li>
                ))}
              </Box>
            </Alert>
          )}

          {result.monthsShifted > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {result.monthsShifted} catch-up month(s) owed a closed period and were posted to the
              earliest open period instead — see each asset's depreciation schedule (Posted to
              column) for detail.
            </Alert>
          )}
        </Paper>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} data-help-page="fixedAssets.runConfirm.page">
        <DialogTitle>Run depreciation?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Posts one journal entry per asset per outstanding month through{' '}
            {period ? monthLabel(period) : 'the selected period'}. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button data-help="fixedAssets.runConfirm.cancel" onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button data-help="fixedAssets.runConfirm.run" variant="contained" onClick={doRun}>Run</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DepreciationRun;
